import {isUndefined} from 'lodash';
import {DateTime} from 'luxon';

import {BusinessHourLimits} from '../../businessHours/helpers/businessHourLimits';

/**
 * Convert a heatmap time in the format day: number + hour: number from
 * one timezone to another. Will wrap times around to new days if neccesary
 */
export const convertTime = (
  hour: number,
  day: number,
  fromZone: string,
  toZone: string,
): {hour: number; day: number} => {
  if (fromZone === toZone) {
    return {hour, day};
  }

  const offsetHour = hour + computeHourOffsetBetweenTimezones(fromZone, toZone);

  if (offsetHour < 0) {
    // we need to reduce the day by one and wrap the hours around midnight
    return {hour: 24 + offsetHour, day: day === 1 ? 7 : day - 1};
  }

  if (offsetHour > 23) {
    // we need to increase the day by one and wrap the hours into the next day
    return {hour: offsetHour - 24, day: day === 7 ? 1 : day + 1};
  }

  return {hour: offsetHour, day};
};

/**
 * Computes if we can support the displaying the heatmap with the business hours
 * shifted to the users current timezone. We don't ever want to wrap hours around midnight
 * (meaning we don't want to have something where the business hours are 4pm - 8am) when using
 * the heatmap because we end up with funky behavior trying to display non-sequential hours.
 */
export const computeIsBusinessHoursCompatibleWithUserTimezone = (
  businessHourLimits: BusinessHourLimits,
  userTimeZone: string,
): boolean => {
  // Always return true if the timezone is undefined - there is
  // no shift and the users hours will never wrap around midnight
  if (isUndefined(businessHourLimits.timezone)) {
    return true;
  }

  // Compute the offset and then shift the business hours to the userTimeZone. If either
  // the open or close time ends up outside of the range 0-23 that means that displaying the
  // heatmap using the users business hours would cause the cells to wrap around midnight -
  // and we want to avoid so we fall back to the defaults in that case
  const offset = computeHourOffsetBetweenTimezones(businessHourLimits.timezone, userTimeZone);
  const opensOffset = businessHourLimits.opensHour + offset;
  const closesOffset = businessHourLimits.closesHour + offset;

  return opensOffset >= 0 && opensOffset <= 23 && closesOffset >= 0 && closesOffset <= 23;
};

/**
 * Calculate the current difference in hours between two timezones. Bases the calculation
 * off of the current time in UTC to account for the shifts in offsets between timezones
 * during the year due to things like daylight savings time.
 */
const computeHourOffsetBetweenTimezones = (fromZone: string, toZone: string): number => {
  const utcIso = DateTime.utc().toISO();
  const fromTime = DateTime.fromISO(utcIso, {zone: fromZone});
  const toTime = DateTime.fromISO(utcIso, {zone: toZone});
  const hoursOffset = Number(toTime.toFormat('HH')) - Number(fromTime.toFormat('HH'));

  return hoursOffset;
};
